import { countryLabels } from '@/constant/countries';
import clsx from 'clsx';
import { useState, useRef, useEffect } from 'react';
import { KeenIcon } from '@/components';

interface NationalitySelectProps {
  id?: string;
  className?: string;
  selected: string;
  setFieldValue: (value: string) => void;
  disabled?: boolean;
  hasError?: boolean;
}

const NationalitySelect = ({
  id,
  className,
  selected,
  setFieldValue,
  disabled,
  hasError,
}: NationalitySelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredCountries = Object.entries(countryLabels).filter(
    ([, { demonym }]) =>
      demonym.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedCountry = selected
    ? countryLabels[selected as keyof typeof countryLabels]
    : null;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code: string) => {
    setFieldValue(code);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  return (
    <div className={clsx('relative w-full', className)} ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={isOpen ? searchTerm : selectedCountry?.demonym || ''}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={handleInputFocus}
          placeholder="Select nationality"
          disabled={disabled}
          className={clsx(
            'input input-bordered w-full h-10 px-3 pr-8 border-gray-300 text-gray-700 text-sm focus:ring-primary focus:border-primary cursor-pointer',
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white',
            hasError && 'border-red-500 focus:ring-red-500'
          )}
          autoComplete="off"
          aria-label="Nationality"
          aria-invalid={hasError}
          readOnly={!isOpen}
        />
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <KeenIcon
            type="filled"
            icon={isOpen ? 'up' : 'down'}
            className="text-gray-500 w-4 h-4"
          />
        </div>
      </div>

      {isOpen && !disabled && (
        <div
          tabIndex={0}
          role="listbox"
          aria-label="Nationality options"
          className="absolute z-50 w-full mt-1 bg-gray-100 border border-gray-300 rounded-md shadow-lg max-h-[150px] overflow-y-auto"
        >
          {filteredCountries.length > 0 ? (
            filteredCountries.map(([code, { demonym }]) => (
              <div
                key={code}
                role="option"
                aria-selected={selected === code}
                onClick={() => handleSelect(code)}
                className={clsx(
                  'px-3 py-2 text-sm cursor-pointer hover:bg-gray-100',
                  selected === code && 'bg-primary text-white hover:bg-primary'
                )}
              >
                {demonym}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500 cursor-default">
              No nationalities found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { NationalitySelect };
